"use client";

import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useCookieConsent } from '@/contexts/cookie-consent-context';
import { 
  Shield, 
  BarChart, 
  Settings as SettingsIcon, 
  Megaphone, 
  CheckCircle2,
  Info 
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

export function CookieSettingsDialog() {
  const { showSettings, setShowSettings, consent, updateConsent, acceptAll, rejectAll } = useCookieConsent();
  
  const [tempConsent, setTempConsent] = useState(consent);

  // Aggiorna il temp consent quando il dialog si apre
  React.useEffect(() => {
    if (showSettings) {
      setTempConsent(consent);
    }
  }, [showSettings, consent]);

  const handleSavePreferences = () => {
    updateConsent(tempConsent);
    setShowSettings(false);
  };

  const handleToggle = (category: 'analytics' | 'functional' | 'marketing', value: boolean) => {
    setTempConsent({
      ...tempConsent,
      [category]: value,
    });
  };

  return (
    <Dialog open={showSettings} onOpenChange={setShowSettings}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <SettingsIcon className="w-6 h-6 text-orange-600" />
            Gestione Preferenze Cookie
          </DialogTitle>
          <DialogDescription>
            Personalizza quali cookie desideri accettare. I cookie tecnici necessari sono sempre attivi 
            in quanto essenziali per il funzionamento del sito.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          
          {/* Cookie Necessari - Sempre attivi */}
          <Card className="border-2 border-green-200 bg-green-50/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Cookie Tecnici Necessari</CardTitle>
                    <CardDescription>Sempre attivi - Essenziali per il funzionamento</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-green-700">SEMPRE ATTIVO</span>
                  <div className="w-11 h-6 bg-green-600 rounded-full p-1 cursor-not-allowed">
                    <div className="w-4 h-4 bg-white rounded-full ml-auto shadow" />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 mb-3">
                Questi cookie sono strettamente necessari per fornire i servizi del sito e non possono 
                essere disabilitati. Gestiscono funzionalità base come la navigazione, l'accesso alle 
                aree protette e la memorizzazione delle tue preferenze sui cookie.
              </p>
              <div className="bg-white border border-green-200 rounded-lg p-3">
                <p className="text-xs text-gray-600 font-semibold mb-2">Esempi:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Gestione sessione utente</li>
                  <li>• Sicurezza e prevenzione frodi (CSRF token)</li>
                  <li>• Memorizzazione preferenze cookie</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Cookie Analitici */}
          <Card className="border-2 border-blue-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                    <BarChart className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Cookie Analitici</CardTitle>
                    <CardDescription>Statistiche e miglioramento del servizio</CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Label htmlFor="analytics" className="sr-only">
                    Cookie Analitici
                  </Label>
                  <Switch
                    id="analytics"
                    checked={tempConsent.analytics}
                    onCheckedChange={(checked) => handleToggle('analytics', checked)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 mb-3">
                Ci aiutano a capire come i visitatori interagiscono con il sito raccogliendo e analizzando 
                informazioni in modo anonimo. Questi dati ci permettono di migliorare costantemente la tua esperienza.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-gray-600 font-semibold mb-2">Esempi di utilizzo:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Google Analytics - Analisi traffico e comportamento utenti</li>
                  <li>• Pagine più visitate e tempo di permanenza</li>
                  <li>• Provenienza geografica visitatori (anonimizzata)</li>
                  <li>• Dispositivi e browser utilizzati</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Cookie Funzionali */}
          <Card className="border-2 border-purple-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                    <SettingsIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Cookie Funzionali</CardTitle>
                    <CardDescription>Personalizzazione e funzionalità avanzate</CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Label htmlFor="functional" className="sr-only">
                    Cookie Funzionali
                  </Label>
                  <Switch
                    id="functional"
                    checked={tempConsent.functional}
                    onCheckedChange={(checked) => handleToggle('functional', checked)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 mb-3">
                Permettono di ricordare le tue scelte (come lingua, regione, login) e fornire 
                funzionalità migliorate e più personalizzate.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-xs text-gray-600 font-semibold mb-2">Esempi di utilizzo:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Memorizzazione preferenze di visualizzazione</li>
                  <li>• Salvataggio moduli compilati parzialmente</li>
                  <li>• Preferenze linguistiche e regionali</li>
                  <li>• Chat di supporto e widget interattivi</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Cookie Marketing */}
          <Card className="border-2 border-orange-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                    <Megaphone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Cookie di Marketing</CardTitle>
                    <CardDescription>Pubblicità personalizzata e remarketing</CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Label htmlFor="marketing" className="sr-only">
                    Cookie di Marketing
                  </Label>
                  <Switch
                    id="marketing"
                    checked={tempConsent.marketing}
                    onCheckedChange={(checked) => handleToggle('marketing', checked)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 mb-3">
                Utilizzati per tracciare i visitatori attraverso diversi siti web per mostrare 
                annunci pubblicitari rilevanti e coinvolgenti per l'utente.
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-xs text-gray-600 font-semibold mb-2">Esempi di utilizzo:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Google Ads e Facebook Pixel per remarketing</li>
                  <li>• Annunci personalizzati in base agli interessi</li>
                  <li>• Tracciamento conversioni da campagne pubblicitarie</li>
                  <li>• Analisi efficacia campagne marketing</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Info aggiuntiva */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex gap-3">
            <Info className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-semibold mb-1">Informazioni sulla privacy</p>
              <p>
                Puoi modificare le tue preferenze in qualsiasi momento. Per maggiori informazioni 
                su come trattiamo i tuoi dati, consulta la nostra{' '}
                <a href="/privacy-policy" className="text-orange-600 hover:text-orange-700 font-semibold underline">
                  Privacy Policy
                </a>{' '}
                e la{' '}
                <a href="/cookie-policy" className="text-orange-600 hover:text-orange-700 font-semibold underline">
                  Cookie Policy
                </a>.
              </p>
            </div>
          </div>
        </div>

        {/* Pulsanti azione */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <Button
            onClick={acceptAll}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-6 rounded-xl flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-5 h-5" />
            Accetta tutti
          </Button>
          
          <Button
            onClick={handleSavePreferences}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6 rounded-xl"
          >
            Salva preferenze
          </Button>
          
          <Button
            onClick={rejectAll}
            variant="outline"
            className="flex-1 border-2 hover:bg-gray-100 font-semibold py-6 rounded-xl"
          >
            Solo necessari
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
